/**
 * Service API pour le catalogue e-commerce (FrontOffice)
 * Routes publiques /api/catalog/*
 */

import { api, ApiResponse } from '../services/api';
import {
  ArticleWithStock,
  ArticleCategory,
  CatalogProductsResponse,
  SuggestionGroup
} from '../types/article';

interface CategoriesResponse {
  categories: ArticleCategory[];
}

export const catalogApi = {
  /**
   * Récupérer tous les produits du catalogue
   * GET /api/catalog/products
   */
  async getProducts(params?: {
    categoryId?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ApiResponse<CatalogProductsResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const query = queryParams.toString();
    return api.get<CatalogProductsResponse>(`/api/catalog/products${query ? `?${query}` : ''}`);
  },

  /**
   * Récupérer les détails d'un produit
   * GET /api/catalog/products/:id
   */
  async getProduct(productId: string): Promise<ApiResponse<ArticleWithStock>> {
    return api.get<ArticleWithStock>(`/api/catalog/products/${productId}`);
  },

  /**
   * Récupérer les groupes de suggestion pour un produit
   * GET /api/catalog/products/:id/suggestions
   */
  async getProductSuggestions(productId: string): Promise<ApiResponse<SuggestionGroup[]>> {
    return api.get<SuggestionGroup[]>(`/api/catalog/products/${productId}/suggestions`);
  },

  /**
   * Récupérer toutes les catégories de navigation
   * GET /api/catalog/categories
   */
  async getCategories(): Promise<ApiResponse<ArticleCategory[]>> {
    return api.get<ArticleCategory[]>('/api/catalog/categories');
  },

  /**
   * Récupérer tous les produits d'une catégorie
   * GET /api/catalog/categories/:id/products
   */
  async getCategoryProducts(categoryId: string): Promise<ApiResponse<ArticleWithStock[]>> {
    return api.get<ArticleWithStock[]>(`/api/catalog/categories/${categoryId}/products`);
  },

  /**
   * Rechercher des produits
   * GET /api/catalog/search?q=query
   */
  async searchProducts(query: string, params?: {
    page?: number;
    pageSize?: number;
  }): Promise<ApiResponse<CatalogProductsResponse & { query: string }>> {
    const queryParams = new URLSearchParams();
    queryParams.append('q', query);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    return api.get<CatalogProductsResponse & { query: string }>(`/api/catalog/search?${queryParams.toString()}`);
  },
};
